import { useState, useMemo } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@retailsync/ui'
import { Button } from '@retailsync/ui'
import { StepIndicator } from '../components/import/StepIndicator'
import { EntitySelectStep } from '../components/import/EntitySelectStep'
import { FileUploadStep } from '../components/import/FileUploadStep'
import { FormatSelectionStep } from '../components/import/FormatSelectionStep'
import { MappingStep } from '../components/import/MappingStep'
import { DataPreviewStep } from '../components/import/DataPreviewStep'
import { ImportStep } from '../components/import/ImportStep'
import { ImportSession } from '@retailsync/shared-types'
import { useAuth } from '../contexts/AuthContext'
import { createConnectorEngine, type EngineProgress } from '../services/connector-engine'

const STEPS = [
  { id: 'entity', title: 'Select Entity', description: 'Choose what to import' },
  { id: 'upload', title: 'Upload File', description: 'Select your data file' },
  { id: 'format', title: 'Select Format', description: 'Choose POS system or format' },
  { id: 'mapping', title: 'Map Fields', description: 'Configure field mappings' },
  { id: 'preview', title: 'Preview Data', description: 'Review transformed data' },
  { id: 'import', title: 'Import Data', description: 'Execute the import' }
]

export function ImportWizard() {
  const { user, tenant } = useAuth()
  const [session, setSession] = useState<ImportSession>({
    currentStep: 'entity'
  })
  const [isProcessing, setIsProcessing] = useState(false)
  const [engineProgress, setEngineProgress] = useState<EngineProgress | null>(null)

  // Create engine scoped to this tenant
  const engine = useMemo(() => {
    if (!tenant?.id || !user?.id) return null
    return createConnectorEngine(
      {
        tenantId: tenant.id,
        branchId: undefined,
        userId: user.id,
        role: user.role,
      },
      (progress) => setEngineProgress(progress),
    )
  }, [tenant?.id, user?.id, user?.role])

  const handleNext = () => {
    const currentIndex = STEPS.findIndex(step => step.id === session.currentStep)
    if (currentIndex < STEPS.length - 1) {
      setSession(prev => ({
        ...prev,
        currentStep: STEPS[currentIndex + 1].id as ImportSession['currentStep']
      }))
    }
  }

  const handleBack = () => {
    const currentIndex = STEPS.findIndex(step => step.id === session.currentStep)
    if (currentIndex > 0) {
      setSession(prev => ({
        ...prev,
        currentStep: STEPS[currentIndex - 1].id as ImportSession['currentStep']
      }))
    }
  }

  const updateSession = (updates: Partial<ImportSession>) => {
    setSession(prev => ({ ...prev, ...updates }))
  }

  const renderCurrentStep = () => {
    switch (session.currentStep) {
      case 'entity':
        return <EntitySelectStep session={session} onUpdate={updateSession} />
      case 'upload':
        return <FileUploadStep session={session} onUpdate={updateSession} engine={engine} setIsProcessing={setIsProcessing} />
      case 'format':
        return <FormatSelectionStep session={session} onUpdate={updateSession} />
      case 'mapping':
        return <MappingStep session={session} onUpdate={updateSession} />
      case 'preview':
        return <DataPreviewStep session={session} onUpdate={updateSession} engine={engine} setIsProcessing={setIsProcessing} />
      case 'import':
        return <ImportStep session={session} onUpdate={updateSession} engine={engine} setIsProcessing={setIsProcessing} />
      default:
        return <FileUploadStep session={session} onUpdate={updateSession} engine={engine} setIsProcessing={setIsProcessing} />
    }
  }

  const canProceed = () => {
    switch (session.currentStep) {
      case 'entity':
        return !!session.entity
      case 'upload':
        return !!session.uploadedFile
      case 'format':
        return !!session.selectedTemplate
      case 'mapping':
        return session.mappings && session.mappings.length > 0
      case 'preview':
        return !session.validationErrors || session.validationErrors.length === 0
      case 'import':
        return true
      default:
        return false
    }
  }

  const isLastStep = session.currentStep === 'import'

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Import Data Wizard</h1>
        <p className="text-gray-600">
          Import data from any POS system into RetailsyncGo using our universal connector
        </p>
      </div>

      {/* Step Indicator */}
      <div className="mb-8">
        <StepIndicator steps={STEPS} currentStep={session.currentStep} />
      </div>

      {/* Current Step Content */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>
            {STEPS.find(step => step.id === session.currentStep)?.title}
          </CardTitle>
          <CardDescription>
            {STEPS.find(step => step.id === session.currentStep)?.description}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {renderCurrentStep()}
        </CardContent>
      </Card>

      {/* Navigation */}
      <div className="flex justify-between">
        <Button
          variant="outline"
          onClick={handleBack}
          disabled={session.currentStep === 'upload' || isProcessing}
        >
          Back
        </Button>

        <Button
          onClick={handleNext}
          disabled={!canProceed() || isProcessing}
        >
          {isLastStep ? 'Start Import' : 'Next'}
        </Button>
      </div>

      {/* Progress Indicator */}
      {engineProgress && engineProgress.phase !== 'idle' && engineProgress.phase !== 'complete' && (
        <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
            <span className="text-blue-800">{engineProgress.message}</span>
          </div>
          <div className="mt-2 w-full bg-blue-200 rounded-full h-1.5">
            <div
              className="bg-blue-600 h-1.5 rounded-full transition-all duration-500"
              style={{ width: `${engineProgress.progress}%` }}
            ></div>
          </div>
        </div>
      )}

      {engineProgress?.phase === 'complete' && (
        <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center">
            <span className="text-green-600 font-medium mr-2">✓</span>
            <span className="text-green-800">{engineProgress.message}</span>
          </div>
        </div>
      )}

      {engineProgress?.phase === 'error' && (
        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center">
            <span className="text-red-600 font-medium mr-2">✗</span>
            <span className="text-red-800">{engineProgress.message}</span>
          </div>
        </div>
      )}
    </div>
  )
}